import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Clock, LogOut, ShieldCheck } from "lucide-react";
import client, { getAccess, refreshSession, endSession } from "../lib/api";

const DEFAULT_IDLE_MS = 30 * 60 * 1000; // 30 minutes of no activity
const COUNTDOWN_S = 90;                  // grace window before auto sign out

// Routes worth remembering so a returning person lands where they left.
// The bare auth pages and the public reader are never remembered.
const RESUMABLE = [
  "/consent", "/welcome", "/onboarding/education", "/onboarding/experience",
  "/onboarding/goal", "/klair", "/home", "/report", "/career", "/roadmap",
  "/learning", "/resume", "/resume/shares", "/ask", "/checkin", "/checkin/delta",
  "/admin/items", "/admin/books", "/admin/seats", "/admin/cohort",
];

function isResumable(path) {
  const p = (path || "").replace(/^\/grow/, "") || "/";
  return RESUMABLE.includes(p);
}

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * SessionManager
 *
 * Two quiet jobs, mounted once at the app root:
 *   1. Remembers the last resumable route on the account so sign-in can
 *      route a returning person back to it.
 *   2. A HubSpot-style inactivity guard: after a stretch of no activity
 *      it warns with a countdown, offers "stay signed in" (which
 *      refreshes the token) or "save and sign out", and if the person
 *      does not respond it saves and signs out on their behalf.
 */
export default function SessionManager() {
  const location = useLocation();
  const nav = useNavigate();
  const [warnOpen, setWarnOpen] = useState(false);
  const [remaining, setRemaining] = useState(COUNTDOWN_S);
  const warnOpenRef = useRef(false);
  const idleTimer = useRef(null);
  const countdownTimer = useRef(null);

  const clearTimers = () => {
    clearTimeout(idleTimer.current);
    clearInterval(countdownTimer.current);
  };

  const saveAndSignOut = useCallback(async () => {
    clearTimers();
    warnOpenRef.current = false;
    setWarnOpen(false);
    const grow = (window.location.pathname || "").startsWith("/grow");
    await endSession();
    nav(grow ? "/grow" : "/", { replace: true });
  }, [nav]);

  const staySignedIn = useCallback(async () => {
    clearTimers();
    warnOpenRef.current = false;
    setWarnOpen(false);
    await refreshSession();
    startIdleWatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openWarning = useCallback(() => {
    if (warnOpenRef.current) return;
    warnOpenRef.current = true;
    setWarnOpen(true);
    setRemaining(COUNTDOWN_S);
    clearInterval(countdownTimer.current);
    countdownTimer.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(countdownTimer.current);
          saveAndSignOut();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  }, [saveAndSignOut]);

  const startIdleWatch = useCallback(() => {
    if (warnOpenRef.current) return;
    clearTimeout(idleTimer.current);
    const idleMs = Number(window.__KLAREET_IDLE_MS__) || DEFAULT_IDLE_MS;
    idleTimer.current = setTimeout(openWarning, idleMs);
  }, [openWarning]);

  // Remember the last resumable route on every navigation.
  useEffect(() => {
    if (getAccess() && isResumable(location.pathname)) {
      client.put("/journey/last-route", { route: location.pathname }).catch(() => {});
    }
  }, [location.pathname]);

  // Idle watch. Re-evaluated on each navigation so it engages right
  // after sign-in and stands down after sign-out.
  useEffect(() => {
    if (!getAccess()) {
      clearTimers();
      return;
    }
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    const onActivity = () => {
      if (!warnOpenRef.current) startIdleWatch();
    };
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    startIdleWatch();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearTimers();
    };
  }, [location.pathname, startIdleWatch]);

  if (!warnOpen) return null;

  return (
    <div className="k-session-overlay" data-testid="session-warning-modal" role="dialog" aria-modal="true">
      <div className="k-session-card k-fade-in">
        <div className="k-session-icon"><Clock size={20} /></div>
        <h2 className="klareet-serif" style={{ fontSize: "1.5rem", lineHeight: 1.15 }} data-testid="session-warning-title">
          Still there?
        </h2>
        <p className="k-quiet mt-2" style={{ lineHeight: 1.55 }}>
          It has been quiet for a bit, so I will pause your session to keep your
          account safe. Your progress is already saved, so nothing is lost.
        </p>
        <div className="k-session-count" data-testid="session-countdown">
          Signing out in <span className="klareet-mono">{fmt(remaining)}</span>
        </div>
        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={staySignedIn}
            className="k-btn k-btn-accent flex-1"
            data-testid="session-stay"
          >
            <ShieldCheck size={16} /> Stay signed in
          </button>
          <button
            type="button"
            onClick={saveAndSignOut}
            className="k-btn k-btn-ghost flex-1"
            data-testid="session-signout"
          >
            <LogOut size={16} /> Save and sign out
          </button>
        </div>
      </div>
    </div>
  );
}
